// General-insurance renewal flow (schema 8): a pipeline on top of the existing `renewals` records —
// requested quotes → compared → sent to client → approved → renewed (or lost) — plus follow-up tasks at 45/30/15 days
// before expiry (days configurable in Settings: `renewals.giDays`).
//
// What counts as general insurance: the policy's `branch` is 'general' (the field every policy has), OR its category
// is one of the general classes (motor, home, travel, liability, business) — so a policy entered with the life branch
// by mistake but categorised as motor still shows. Life categories (life, medical, savings, pension, …) never do.
// Expiry: the policy's renewal date (an expired, unrenewed date stays until decided), else the next anniversary.
//
// Every stage change is a domain command and a timeline entry on the client. "Renewed" and "lost" close the renewal
// through the existing decideRenewal (premium from the renewal date, renewal production once; lost = not renewed).
import { stableId } from './ids.ts';
import { mergeChangesets, type Changeset, type Put, type WorkspaceView } from './changeset.ts';
import { DomainError, livePolicy, created, opt, put, touched, type Ctx } from './cmd.ts';
import { addDays, diffDays, type Day } from './dates.ts';
import { GI_RENEWAL_STAGES, type GiRenewalStage, type Policy, type PolicyCategory, type Renewal, type RowTypes, type TableName } from './schema.ts';
import { isActivePolicy, nextPolicyAnniversary } from './rules.ts';
import { annualNow, currencyOf } from './policy-engine.ts';
import { decideRenewal } from './business.ts';
import { getSetting } from './services.ts';

export const GENERAL_CATEGORIES: readonly PolicyCategory[] = ['motor', 'home', 'travel', 'liability', 'business'];
export const DEFAULT_GI_RENEWAL_DAYS: readonly number[] = [45, 30, 15];
export const GI_OPEN_STAGES = GI_RENEWAL_STAGES.filter((s) => s !== 'renewed' && s !== 'lost');

/** general insurance = branch 'general', or a general category (motor, home, travel, liability, business) */
export function isGeneralPolicy(p: Pick<Policy, 'branch' | 'category'>): boolean {
  return p.branch === 'general' || (!!p.category && GENERAL_CATEGORIES.includes(p.category));
}
/** the follow-up days before expiry (Settings `renewals.giDays`, default 45/30/15), largest first */
export function giRenewalDays(v: WorkspaceView): number[] {
  const raw = getSetting<unknown>(v, 'renewals.giDays', DEFAULT_GI_RENEWAL_DAYS);
  const list = (Array.isArray(raw) ? raw : []).map(Number).filter((n) => Number.isInteger(n) && n > 0 && n <= 365);
  return [...new Set(list.length ? list : DEFAULT_GI_RENEWAL_DAYS)].sort((a, b) => b - a);
}
/** the expiry this renewal is about (an unrenewed past renewal date stays the expiry until it is decided) */
export function giExpiry(v: WorkspaceView, p: Policy, today: Day): Day | null {
  if (p.renewalDate && p.renewalDate < today && !v.where('renewals', 'policyId', p.id).some((r) => r.dueDate === p.renewalDate && (r.status === 'renewed' || r.status === 'lapsed' || r.status === 'declined'))) return p.renewalDate;
  return nextPolicyAnniversary(p, today);
}

export interface GiRenewalItem { policy: Policy; personId: string; dueDate: Day; days: number; renewal?: Renewal; stage: GiRenewalStage | null; renewalPremium: number | null; lastPremium: number | null; currency: string }
/**
 * Open general-insurance renewals: expiring within `horizon` days (default: the largest follow-up day, at least 45)
 * or expired up to 60 days ago, not yet decided. `stage` null = not started.
 */
export function generalRenewalPipeline(v: WorkspaceView, today: Day, opts: { horizon?: number; includeClosed?: boolean } = {}): GiRenewalItem[] {
  const horizon = opts.horizon ?? Math.max(45, ...giRenewalDays(v));
  const out: GiRenewalItem[] = [];
  for (const p of v.rows('policies')) {
    if (!isActivePolicy(p) || !isGeneralPolicy(p)) continue;
    const person = v.get('persons', p.personId);
    if (!person || person.archivedAt) continue;
    const due = giExpiry(v, p, today);
    if (!due) continue;
    const days = diffDays(today, due);
    if (days > horizon || days < -60) continue;
    const renewal = v.where('renewals', 'policyId', p.id).find((r) => r.dueDate === due);
    const closed = !!renewal && (renewal.status === 'renewed' || renewal.status === 'lapsed' || renewal.status === 'declined');
    if (closed && !opts.includeClosed) continue;
    const stage = renewal?.stage ?? (renewal?.status === 'renewed' ? 'renewed' : closed ? 'lost' : null);
    out.push({ policy: p, personId: p.personId, dueDate: due, days, renewal, stage, renewalPremium: renewal?.renewalPremium ?? null, lastPremium: renewal?.lastPremium ?? (annualNow(v, p, today) || null), currency: currencyOf(p, null) });
  }
  return out.sort((a, b) => a.days - b.days);
}

/** a view that also sees rows of a changeset not yet written (to chain commands in one changeset) */
function overlay(v: WorkspaceView, puts: Put[]): WorkspaceView {
  const pending = (t: TableName) => puts.filter((p) => p.table === t).map((p) => p.row as unknown as { id: string });
  return {
    rows: <T extends TableName>(t: T) => { const extra = pending(t); return [...v.rows(t).filter((r) => !extra.some((x) => x.id === (r as { id: string }).id)), ...extra] as unknown as RowTypes[T][]; },
    get: <T extends TableName>(t: T, id: string | null | undefined) => ((id && pending(t).find((x) => x.id === id)) || v.get(t, id)) as RowTypes[T] | undefined,
    where: <T extends TableName>(t: T, f: keyof RowTypes[T], val: unknown) => { const extra = pending(t); return [...v.where(t, f, val).filter((r) => !extra.some((x) => x.id === (r as { id: string }).id)), ...(extra as unknown as RowTypes[T][]).filter((r) => (r as unknown as Record<string, unknown>)[f as string] === val)]; },
  };
}

export interface GiStageInput { policyId: string; dueDate?: Day | null; stage: GiRenewalStage; renewalPremium?: number | null; notes?: string | null }
/**
 * Move a general-insurance renewal to a stage. Creates the renewal record for this expiry when there is none.
 * Renewed → the existing renewal decision (premium, production once); lost → recorded as not renewed. A closed
 * renewal cannot move again. Every move is written on the client's timeline.
 */
export function moveGeneralRenewal(v: WorkspaceView, input: GiStageInput, ctx: Ctx): Changeset {
  const p = livePolicy(v, input.policyId);
  if (!isGeneralPolicy(p)) throw new DomainError('gi_renewal.not_general');
  if (!(GI_RENEWAL_STAGES as readonly string[]).includes(input.stage)) throw new DomainError('gi_renewal.stage');
  const due = input.dueDate ?? giExpiry(v, p, ctx.today);
  if (!due) throw new DomainError('gi_renewal.no_date');
  if (input.renewalPremium != null && (!Number.isFinite(input.renewalPremium) || input.renewalPremium < 0)) throw new DomainError('renewal.negative');
  const cur = v.where('renewals', 'policyId', p.id).find((r) => r.dueDate === due);
  if (cur && (cur.status === 'renewed' || cur.status === 'lapsed' || cur.status === 'declined')) throw new DomainError('gi_renewal.closed');
  if (cur?.stage === input.stage) throw new DomainError('gi_renewal.same_stage');
  const premium = input.renewalPremium !== undefined ? input.renewalPremium : cur?.renewalPremium ?? null;
  const fields = {
    stage: input.stage, stageAt: ctx.now, renewalPremium: premium, notes: input.notes !== undefined ? opt(input.notes) ?? cur?.notes ?? null : cur?.notes ?? null,
    status: (premium != null && (cur?.status ?? 'pending') === 'pending' ? 'quoted' : cur?.status ?? 'pending') as Renewal['status'],
  };
  const row: Renewal = cur ? touched(ctx, cur, fields) : created<'renewals'>(ctx, { policyId: p.id, personId: p.personId, dueDate: due, lastPremium: annualNow(v, p, ctx.today) || null, currency: currencyOf(p, ctx.region), quotationId: null, remindedAt: null, decidedAt: null, ...fields });
  const puts: Put[] = [put('renewals', row)];
  if (input.stage === 'renewed' || input.stage === 'lost') {
    const decided = decideRenewal(overlay(v, puts), row.id, input.stage === 'renewed' ? 'renewed' : 'lapsed', ctx);
    // decideRenewal re-writes the renewal row (status, decidedAt): keep our stage on it
    const merged = decided.puts.map((x) => (x.table === 'renewals' ? { ...x, row: { ...(x.row as Renewal), stage: input.stage, stageAt: ctx.now, version: row.version } } as Put : x));
    // its timeline entry gets the stage so the client's history shows the pipeline step
    for (const x of merged) if (x.table === 'timeline') (x.row as { data: Record<string, unknown> }).data = { ...((x.row as { data?: Record<string, unknown> }).data ?? {}), stage: input.stage, from: cur?.stage ?? null };
    return { action: `gi_renewal.${input.stage}`, entity: 'renewals', entityId: row.id, summary: p.productName, puts: merged, deletes: [] };
  }
  puts.push(put('timeline', created<'timeline'>(ctx, { personId: p.personId, at: ctx.now, type: 'policy', title: `timeline.gi_renewal_${input.stage}`, body: p.productName, refTable: 'policies', refId: p.id, data: { kind: 'gi_renewal', stage: input.stage, from: cur?.stage ?? null, dueDate: due, renewalPremium: premium } })));
  return { action: `gi_renewal.${input.stage}`, entity: 'renewals', entityId: row.id, summary: p.productName, puts, deletes: [] };
}

/** the deterministic id of a follow-up (two devices create the same task, never two) */
export const giRenewalTaskId = (policyId: string, dueDate: Day, days: number) => stableId('gi_renewal_task', `${policyId}:${dueDate}:${days}`);
export interface GiTaskPlan { policy: Policy; dueDate: Day; days: number; taskDue: Day; id: string }
/**
 * Which follow-ups are due now: for each open general renewal, the tightest threshold already reached (e.g. 30 when
 * 29 days remain) — earlier thresholds that were missed are not back-filled. Nothing for a decided renewal.
 */
export function planGeneralRenewalTasks(v: WorkspaceView, today: Day): GiTaskPlan[] {
  const days = giRenewalDays(v);
  const out: GiTaskPlan[] = [];
  for (const it of generalRenewalPipeline(v, today, { horizon: Math.max(...days) })) {
    if (it.days < 0) continue;
    const reached = days.filter((d) => it.days <= d);
    if (!reached.length) continue;
    const d = Math.min(...reached);
    const id = giRenewalTaskId(it.policy.id, it.dueDate, d);
    if (v.get('tasks', id)) continue;
    const taskDue = addDays(it.dueDate, -d);
    out.push({ policy: it.policy, dueDate: it.dueDate, days: d, taskDue: taskDue > today ? taskDue : today, id });
  }
  return out;
}
/** create the follow-ups that are due (idempotent; `title` lets the UI pass translated text) */
export function createGeneralRenewalTasks(v: WorkspaceView, ctx: Ctx, title?: (plan: GiTaskPlan) => string): Changeset {
  const plans = planGeneralRenewalTasks(v, ctx.today);
  const parts: Changeset[] = plans.map((pl) => ({
    action: 'gi_renewal.task', deletes: [], puts: [put('tasks', created<'tasks'>(ctx, {
      id: pl.id, kind: 'renewal', title: title ? title(pl) : `Renewal in ${pl.days} days: ${pl.policy.productName}`, personId: pl.policy.personId, prospectId: null, caseId: null, policyId: pl.policy.id,
      dueDate: pl.taskDue, dueTime: null, durationMin: null, locationType: null, location: null, status: 'open', priority: pl.days <= 15 ? 'high' : 'normal', notes: null,
      origin: 'gi_renewal', data: { policyId: pl.policy.id, dueDate: pl.dueDate, days: pl.days },
    }))],
  }));
  const cs = mergeChangesets('gi_renewal.tasks', ...parts);
  cs.summary = String(plans.length);
  return cs;
}
